import * as React from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { dateFormatter, DATE_ONLY_FORMAT } from '../../utils/DataFormatterUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { ArrowRightOutlined, SearchOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import WorkOrderDisplayStatusDisplay from '../work_order_display_status_display/WorkOrderDisplayStatusDisplay';
import WorkOrderStatusDisplay from '../work_order_status_display/WorkOrderStatusDisplay';
import OWLink from '../OWLink';
import moment from 'moment';

require('./ServiceCallPopoverDisplay.less');

export const ServiceCallPopoverDisplayV2: React.FC<any> = ({ workOrder }) => {
	if (!workOrder) {
		return null;
	}

	const lastServiceCall = nullSafeGetOrElse('lastServiceCall', workOrder, {});
	const checkInTime = nullSafeGet('checkInTime', lastServiceCall);
	const checkOutTime = nullSafeGet('checkOutTime', lastServiceCall);
	const serviceScheduledAt = nullSafeGet('serviceScheduledAt', lastServiceCall);

	let label = 'Scheduled';
	let timeDisplay = '';

	if (checkOutTime && checkInTime) {
		// Both check-in and check-out exist
		label = 'Service time';
		const duration = moment(checkInTime).from(moment(checkOutTime), true);

		timeDisplay = `${dateFormatter(checkInTime, 'dddd, MMMM D')}, ${dateFormatter(
			checkInTime,
			'h:mm a'
		)} - ${dateFormatter(checkOutTime, 'h:mm a')} (${duration})`;
	} else if (checkInTime) {
		// Only check-in exists
		label = 'Checked in';
		timeDisplay = `${dateFormatter(checkInTime, 'dddd, MMMM D')}, ${dateFormatter(
			checkInTime,
			'h:mm a'
		)}`;
	} else if (serviceScheduledAt) {
		// Fall back to scheduled time
		label = 'Scheduled';
		timeDisplay = `${dateFormatter(serviceScheduledAt, 'dddd, MMMM D')}, ${dateFormatter(
			serviceScheduledAt,
			'h:mm a'
		)}`;
	}

	return (
		<div className="flex min-w-[400px] max-w-[50vw] flex-col gap-3 px-2 py-1">
			<div className="flex flex-col gap-3">
				<div className="flex flex-row items-center gap-2">
					<WorkOrderDisplayStatusDisplay status={nullSafeGet('displayStatus', workOrder)} />
					<WorkOrderStatusDisplay
						displayStatus={nullSafeGet('displayStatus', workOrder)}
						status={nullSafeGet('status', workOrder)}
						showDisplayStatus={false}
					/>
				</div>
				<div className="flex flex-col gap-2">
					<div className="h5medium text-character-primary">{nullSafeGet('title', workOrder)}</div>
					<div className="flex flex-row items-center gap-1">
						<span className="bodyregular text-character-secondary">
							{label}: {timeDisplay}
						</span>
					</div>
				</div>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<SearchOutlined /> WO #{nullSafeGet('id', workOrder)}
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className={`icons8-font icons8-location-logo-2`} />
				</div>
				<div>{nullSafeGet('location.name', workOrder)}</div>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className={`icons8-font icons8-company-2`} />
				</div>
				<div>{nullSafeGet('supplierFacility.displayName', workOrder)}</div>
			</div>
			{nullSafeGet('lastServiceCall.leadTechnicianEmailContact.nameGiven', workOrder) ? (
				<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
					<div>
						<i className={`icons8-font icons8-worker`} />
					</div>
					<div>
						{nullSafeGetOrElse('lastServiceCall.additionalTechnicians', workOrder, []).length >
						0 ? (
							<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
								{nullSafeGetOrElse('lastServiceCall.additionalTechnicians', workOrder, []).length +
									1}{' '}
								technicians
							</div>
						) : null}
						{nullSafeGet('lastServiceCall.leadTechnicianEmailContact', workOrder) ? (
							<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
								<ContactAvatar
									size="small"
									hidePopover={true}
									contact={nullSafeGet('lastServiceCall.leadTechnicianEmailContact', workOrder)}
									userType={ROLE_TYPES.BUYER}
								/>
								<div>
									{nullSafeGet('lastServiceCall.leadTechnicianEmailContact.nameGiven', workOrder)}{' '}
									{nullSafeGet('lastServiceCall.leadTechnicianEmailContact.nameFamily', workOrder)}
								</div>
							</div>
						) : null}
						{nullSafeGet('lastServiceCall.additionalTechnicians', workOrder).map((contact) => (
							<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
								<ContactAvatar
									size="small"
									hidePopover={true}
									contact={contact}
									userType={ROLE_TYPES.BUYER}
								/>
								<div>
									{contact.nameGiven} {contact.nameFamily}
								</div>
							</div>
						))}
					</div>
				</div>
			) : null}
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className={`icons8-font icons8-engineer-symbol`} />
				</div>
				<div>{nullSafeGet('problemType.hierarchyName', workOrder)}</div>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className={`icons8-font icons8-service-bell`} />
				</div>
				<div>{nullSafeGet('woPriority.name', workOrder)}</div>
			</div>
			<div className="bodyregular flex flex-row items-center gap-4 text-character-secondary">
				<div>
					<i className="icons8-font icons8-green-check-mark"></i>
				</div>
				<div>
					Est. Completion{' '}
					{dateFormatter(
						nullSafeGetOrElse(
							'estimatedCompletionDate',
							workOrder,
							nullSafeGet('dueDate', workOrder)
						),
						DATE_ONLY_FORMAT
					)}
				</div>
			</div>
			<OWLink
				preserveBackLinkStates={true}
				backText="Back to service schedule"
				to={`/supplier/workOrders/detail/${nullSafeGet('id', workOrder)}/details`}
				className="w-full"
				target="_blank"
			>
				<Button className="w-full" icon={<ArrowRightOutlined />}>
					View Work Order
				</Button>
			</OWLink>
		</div>
	);
};
